import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api, img } from "../../lib/api";
import { useSeo, pageSeo, breadcrumbLd } from "../../lib/seo";
import { ExperienceCard, PageHero, Breadcrumbs, Empty, Spinner } from "../../components/site/Bits";
import NotFound from "./NotFound";

export function Destinations() {
  const { site } = useSite();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/public/destinations").then((r) => setItems(r.data)); }, []);
  useSeo(pageSeo(site, "destinations", { title: "Destinations", description: "Mountains, forts, coastline and lakes — explore destinations and their experiences." }), [site]);
  return (
    <>
      <PageHero eyebrow="Explore" title="Destinations" text="Mountains, forts, coastline and lakes — pick a place and see what's on." image="/images/rajmachi.jpg" />
      <section className="py-20 bg-white"><div className="container-x">
        {!items ? <Spinner /> : !items.length ? <Empty title="No destinations yet" /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="destinations-grid">
            {items.map((d) => (
              <Link key={d.id} to={`/destinations/${d.slug}`} className="card img-zoom group" data-testid={`destination-${d.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F5F5F5]"><img src={img(d.image)} alt={d.image_alt || d.name} loading="lazy" className="h-full w-full object-cover" /></div>
                <div className="p-5"><p className="eyebrow">{d.type} · {d.experience_count} experience{d.experience_count === 1 ? "" : "s"}</p><h2 className="font-display text-2xl font-bold mt-2 group-hover:underline">{d.name}</h2><p className="text-sm text-[#666] mt-2">{d.short_description}</p></div>
              </Link>
            ))}
          </div>
        )}
      </div></section>
    </>
  );
}

export function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setD(null); api.get(`/public/destinations/${slug}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo(d ? { title: d.meta_title || d.name, description: d.meta_description || d.short_description, canonical: d.canonical_url || undefined, robots: d.robots, ogImage: d.image,
    jsonLd: breadcrumbLd([{ name: "Home", to: "/" }, { name: "Destinations", to: "/destinations" }, { name: d.name, to: `/destinations/${d.slug}` }]) } : { title: "Destination" }, [d]);
  if (err) return <NotFound />;
  if (!d) return <Spinner />;
  return (
    <>
      <PageHero eyebrow={d.region} title={d.name} text={d.short_description} image={d.image} alt={d.image_alt} />
      <div className="container-x pt-6"><Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Destinations", to: "/destinations" }, { name: d.name }]} /></div>
      <section className="py-16 bg-white"><div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4"><p className="eyebrow mb-3 flex items-center gap-1"><MapPin className="h-3 w-3" />{d.type}</p><p className="text-[#333] leading-relaxed whitespace-pre-line" data-testid="destination-description">{d.description}</p></div>
        <div className="lg:col-span-8">
          <h2 className="section-title mb-8">Experiences in {d.name}</h2>
          {!d.experiences.length ? <Empty title="No experiences listed yet" text="Contact us to plan a custom trip here." /> : <div className="grid gap-6 sm:grid-cols-2">{d.experiences.map((e, i) => <ExperienceCard key={e.id} e={e} i={i} />)}</div>}
        </div>
      </div></section>
    </>
  );
}
